import { Injectable } from '@angular/core';

import { TranslateService } from '@ngx-translate/core';
import { EMPTY, forkJoin, Observable, of, take, tap, throwError, timeout, timer } from 'rxjs';
import { catchError, concatMap, expand, filter, map, switchMap, takeUntil, takeWhile } from 'rxjs/operators';

import { ExportTagType, ShToastFactory, Toast } from '@stylehaven/components';
import { ExportEntity } from '../../entities/downloads/export-entity.entity';
import { ExportJob } from '../../entities/downloads/export-job.entity';
import { ExportScope } from '../../entities/downloads/export-scope.entity';
import { QuickExportContentMode, QuickExportRequest } from '../../entities/downloads/quick-export-request.entity';
import { ExportStartPayload } from '../../export-sidepanel/export-sidepanel.component';
import {
    RejectionReport,
    toRejectionReportRequest
} from '../../entities/downloads/rejection-report-request.entity';
import { FileSaverService } from '../../services/file-saver/file-saver.service';
import { UserService } from '../../services/user/user.service';
import { ToastHelper } from '../../toast/service/toast.helper';
import { classifyCreateFailure, toFailureToastKey } from '../helpers/export-failure.helper';
import { DelenProgress, MeldingState, toMeldingState } from '../helpers/export-melding.helper';
import { classifyExportStatus, ExportStatusClass, isExportSettled } from '../helpers/export-status.helper';
import {
    CreateOutcome,
    toAcceptedPollDelay,
    toAcceptedProgress,
    toCreateOutcome,
    toNamedExports
} from '../helpers/quick-export-create.helper';
import { classifyReportFailure, toReportFilename, toReportStamp } from '../helpers/rejection-report.helper';
import {
    busyMelding,
    busyMeldingUpdate,
    failureMelding,
    failureMeldingToast,
    followUpMelding,
    meldingToast,
    reportBusyMelding,
    reportFailureMelding,
    reportNoneMelding,
    reportNoPermissionMelding,
    reportRejectionsMelding
} from '../helpers/quick-export-toast.helper';
import { QuickExportService } from '../services/quick-export.service';

interface AcceptedPoll {
    readonly progress: DelenProgress;
    readonly found: ExportEntity[] | null;
    readonly elapsed: number;
}

/** Map the panel's content-mode booleans onto the backend `ContentMode` (HFBD-53 ticket 001). */
export function toQuickExportContentMode(payload: ExportStartPayload): QuickExportContentMode {
    if (payload.includeFiles && payload.includeMetadata) {
        return 'METADATA_AND_FILES';
    }
    if (payload.includeFiles) {
        return 'FILES_ONLY';
    }
    return 'METADATA_ONLY';
}

/**
 * Fires a quick export for the chosen scope and reports the progress of every deel through one melding.
 * `trigger()` resolves on the create call; nothing downloads by itself.
 */
@Injectable()
export class QuickExportSandboxService {
    private static readonly POLL_INTERVAL: number = 5000;
    private static readonly SLOW_AFTER: number = 30 * 60 * 1000;
    private static readonly RECENT_EXPORTS: number = 20;
    private static readonly RECENT_MARGIN: number = 10;
    private static readonly GIVE_UP_AFTER: number = 30 * 60 * 1000;
    private static readonly PREFIX: string = 'exports.notifications.quick-export';
    private static readonly REPORT_PREFIX: string = 'exports.notifications.rejection-report';
    private static readonly REPORT_TIMEOUT: number = 2 * 60 * 1000;
    private static readonly REPORT_FUNCTION: string = 'VIEW_DOSSIER';

    constructor(
        private readonly service: QuickExportService,
        private readonly toastHelper: ToastHelper,
        private readonly toastFactory: ShToastFactory,
        private readonly translateService: TranslateService,
        private readonly userService: UserService,
        private readonly fileSaver: FileSaverService
    ) {}

    trigger(scope: ExportScope, payload: ExportStartPayload): Observable<ExportJob[]> {
        const request: QuickExportRequest = {
            ...(scope.kind === 'filtered' ? { Q: scope.q } : { SubjectIds: scope.subjectIds }),
            ContentMode: toQuickExportContentMode(payload),
            Tag: ExportTagType.Download
        };

        const toast: Toast = busyMelding(this.toastFactory, this.messageFor(toMeldingState(this.pending())));
        this.toastHelper.addToast(toast);

        // The foutenrapport is a separate call on the same scope: it runs in parallel, independent of the create call.
        this.fetchRejectionReport(scope, toReportStamp(Date.now()));

        return this.service.create(request).pipe(
            take(1),
            map(response => ({ outcome: toCreateOutcome(response), jobs: response.body ?? [] })),
            tap(({ outcome }) => this.track(outcome, toast.id)),
            map(({ jobs }) => jobs),
            catchError(err => {
                this.toastHelper.updateToast(
                    toast.id,
                    failureMelding(this.translate(toFailureToastKey(classifyCreateFailure(err))))
                );
                return throwError(() => err);
            })
        );
    }

    notifyFailed(): void {
        this.toastHelper.addToast(failureMeldingToast(this.toastFactory, this.translate('failed')));
    }

    private track(outcome: CreateOutcome, toastId: number): void {
        if (outcome.kind === 'created') {
            this.trackCompletion(outcome.jobs.map(job => job.Id), toastId);
            return;
        }

        this.trackAccepted(outcome.names, toastId);
    }

    // #region A 202: the delen are only known by name
    /**
     * Looks the named delen up in the downloads list, polling less often as time passes
     * ({@link toAcceptedPollDelay}). It says straight away that it takes longer than expected. Once every name
     * is listed, their ids go to {@link trackCompletion}, as after a 201; while any name is still missing when
     * {@link GIVE_UP_AFTER} passes, it hands over to a warning instead.
     */
    private trackAccepted(names: string[], toastId: number): void {
        if (!names.length) {
            this.toastHelper.updateToast(toastId, failureMelding(this.translate('failed')));
            return;
        }

        this.toastHelper.updateToast(toastId, busyMeldingUpdate(this.translate('accepted')));

        const size: number = Math.max(
            QuickExportSandboxService.RECENT_EXPORTS,
            names.length + QuickExportSandboxService.RECENT_MARGIN
        );
        const startedAt: number = Date.now();
        let latest: ExportEntity[] = [];

        const poll = (): Observable<AcceptedPoll> =>
            this.service.listRecentExports(size).pipe(
                take(1),
                catchError(() => of(latest)),
                tap(exports => (latest = exports)),
                map(exports => ({
                    progress: toAcceptedProgress(names, exports),
                    found: toNamedExports(names, exports),
                    elapsed: Date.now() - startedAt
                }))
            );

        poll()
            .pipe(
                expand(({ found, elapsed }) => {
                    if (found || elapsed >= QuickExportSandboxService.GIVE_UP_AFTER) {
                        return EMPTY;
                    }

                    const delay: number = Math.min(
                        toAcceptedPollDelay(elapsed),
                        QuickExportSandboxService.GIVE_UP_AFTER - elapsed
                    );

                    return timer(delay).pipe(switchMap(() => poll()));
                }),
                takeUntil(this.dismissed(toastId))
            )
            .subscribe(({ progress, found, elapsed }) => {
                if (found) {
                    this.trackCompletion(found.map(entry => entry.ExportJobId), toastId);
                    return;
                }

                this.renderAccepted(toastId, progress, elapsed);
            });
    }

    private renderAccepted(toastId: number, progress: DelenProgress, elapsed: number): void {
        if (elapsed >= QuickExportSandboxService.GIVE_UP_AFTER) {
            this.toastHelper.updateToast(
                toastId,
                followUpMelding(this.translate('accepted-untracked'), this.translate('link'))
            );
            return;
        }

        this.toastHelper.updateToast(toastId, busyMeldingUpdate(this.acceptedMessage(progress)));
    }

    /** Names how many delen are klaar once there are any, so a long wait still shows it moves. */
    private acceptedMessage(progress: DelenProgress): string {
        if (progress.done === 0) {
            return this.translate('accepted');
        }

        return this.messageFor({
            key: 'accepted-progress',
            params: { done: progress.done, total: progress.total ?? 0 },
            kind: 'busy',
            withLink: false
        });
    }
    // #endregion

    private trackCompletion(ids: string[], toastId: number): void {
        if (!ids.length) {
            this.toastHelper.updateToast(toastId, failureMelding(this.translate('failed')));
            return;
        }

        const total: number = ids.length;
        const settled: Map<string, ExportStatusClass> = new Map();
        const startedAt: number = Date.now();
        let openIds: string[] = ids;

        this.renderMelding(toastId, this.progressOf(total, settled, openIds, false));

        timer(0, QuickExportSandboxService.POLL_INTERVAL)
            .pipe(
                concatMap(() => this.pollDelen(openIds)),
                map(polled => {
                    polled
                        .filter(job => isExportSettled(job.Status))
                        .forEach(job => settled.set(job.Id, classifyExportStatus(job.Status)));
                    openIds = openIds.filter(id => !settled.has(id));

                    const elapsed: number = Date.now() - startedAt;

                    return this.progressOf(total, settled, openIds, elapsed >= QuickExportSandboxService.SLOW_AFTER);
                }),
                takeWhile(progress => progress.open > 0, true),
                takeUntil(this.dismissed(toastId))
            )
            .subscribe(progress => this.renderMelding(toastId, progress));
    }

    private pollDelen(openIds: string[]): Observable<ExportJob[]> {
        if (openIds.length === 0) {
            return of([]);
        }

        return forkJoin(
            openIds.map(id =>
                this.service.getExport(id).pipe(
                    take(1),
                    catchError(() => of(null))
                )
            )
        ).pipe(map(results => results.filter((job): job is ExportJob => !!job)));
    }

    private progressOf(
        total: number,
        settled: Map<string, ExportStatusClass>,
        openIds: string[],
        slow: boolean
    ): DelenProgress {
        const classes: ExportStatusClass[] = [...settled.values()];

        return {
            total,
            done: classes.filter(status => status === 'klaar').length,
            failed: classes.filter(status => status === 'mislukt').length,
            open: openIds.length,
            slow
        };
    }

    private pending(): DelenProgress {
        return { total: null, done: 0, failed: 0, open: 0, slow: false };
    }

    private renderMelding(toastId: number, progress: DelenProgress): void {
        const state: MeldingState = toMeldingState(progress);
        const message: string = this.messageFor(state);

        if (state.kind === 'busy') {
            this.toastHelper.updateToast(toastId, busyMeldingUpdate(message));
            return;
        }

        this.toastHelper.updateToast(toastId, meldingToast(state, message, this.translate('link')));
    }

    private dismissed(toastId: number): Observable<void> {
        return this.toastHelper.toasts$.pipe(
            filter(toasts => !toasts.some(toast => toast.id === toastId)),
            take(1),
            map(() => undefined)
        );
    }

    private messageFor(state: MeldingState): string {
        return this.translateService.instant(`${QuickExportSandboxService.PREFIX}.${state.key}`, state.params);
    }

    // #region The foutenrapport
    /** `stamp` is the moment of the request, which names the saved file. */
    private fetchRejectionReport(scope: ExportScope, stamp: string): void {
        this.mayReadRejectionReport()
            .pipe(
                take(1),
                switchMap(allowed => {
                    if (!allowed) {
                        this.toastHelper.addToast(
                            reportNoPermissionMelding(this.toastFactory, this.reportText('no-permission'))
                        );
                        return of(null);
                    }

                    return this.runRejectionReport(scope, stamp);
                })
            )
            .subscribe();
    }

    private runRejectionReport(scope: ExportScope, stamp: string): Observable<RejectionReport | null> {
        const toast: Toast = reportBusyMelding(this.toastFactory, this.reportText('busy'));
        this.toastHelper.addToast(toast);

        return this.service.getRejectionReport(toRejectionReportRequest(scope)).pipe(
            take(1),
            timeout(QuickExportSandboxService.REPORT_TIMEOUT),
            tap(report => this.onRejectionReport(report, stamp, toast.id)),
            catchError(err => {
                this.toastHelper.updateToast(
                    toast.id,
                    reportFailureMelding(this.reportText(classifyReportFailure(err) === 'too-large' ? 'too-large' : 'failed'))
                );
                return of(null);
            })
        );
    }

    private onRejectionReport(report: RejectionReport, stamp: string, toastId: number): void {
        if (!report.hasRejections) {
            this.toastHelper.updateToast(toastId, reportNoneMelding(this.reportText('none')));
            return;
        }

        const filename: string = toReportFilename(stamp);
        this.fileSaver.save(new Blob([report.csv], { type: 'text/csv;charset=utf-8' }), filename);

        this.toastHelper.updateToast(
            toastId,
            reportRejectionsMelding(this.reportText('rejections', { filename }))
        );
    }

    private mayReadRejectionReport(): Observable<boolean> {
        return this.userService.currentUser$.pipe(
            map(user => (user?.Roles?.[0]?.Functions ?? []).includes(QuickExportSandboxService.REPORT_FUNCTION)),
            catchError(() => of(false))
        );
    }

    private reportText(key: string, params?: object): string {
        return this.translateService.instant(`${QuickExportSandboxService.REPORT_PREFIX}.${key}`, params);
    }
    // #endregion

    private translate(key: string): string {
        return this.translateService.instant(`${QuickExportSandboxService.PREFIX}.${key}`);
    }
}
